import * as THREE from 'three';
import type { Game, GameContext, GameMeta, InputState } from '../types';
import {
  createThreeKit,
  disposeObject,
  makeGlowDisc,
  makeStars,
  resizeThree,
  type ThreeKit,
} from '../shared/threeKit';
import { OceanWave } from '../shared/oceanWave';
import { OrbitRig } from '../shared/orbitRig';

type Hazard = { mesh: THREE.Group; x: number; lane: number; r: number };

/**
 * WAVE SURF — ride smooth ocean waves, mouse-orbit the view.
 */
export class WaveDodgeGame implements Game {
  readonly meta: GameMeta = {
    id: 'wave',
    title: 'WAVE SURF',
    blurb: 'Ride rolling sound-waves. Mouse-drag camera. Lean & hop.',
    tag: 'SURF',
    status: 'playable',
  };

  private ctx!: GameContext;
  private kit!: ThreeKit;
  private stars!: THREE.Points;
  private surf!: THREE.Group;
  private board!: THREE.Mesh;
  private body!: THREE.Mesh;
  private glow!: THREE.Mesh;
  private ribbon!: THREE.Mesh;
  private spray!: THREE.Points;
  private ocean = new OceanWave(64);
  private orbit = new OrbitRig({ azimuth: 0.85, elevation: 0.42, distance: 18 });
  private hazards: Hazard[] = [];
  private lane = 0;
  private hopY = 0;
  private hopV = 0;
  private hopping = false;
  private invuln = 0;
  private spawnCd = 0.8;
  private score = 0;
  private lives = 3;
  private wave = 1;
  private message = '←→ LEAN · FIRE HOP · DRAG CAM';
  private messageT = 2.4;
  private dist = 0;
  private disposed = false;
  private readonly segs = 64;
  private readonly rideX = -4;
  private surfaceY = 0;
  private camTarget = new THREE.Vector3();

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;
    this.kit = createThreeKit(ctx, {
      label: 'Wave Surf',
      z: 18,
      fov: 42,
      clear: 0x021018,
      fog: 0x021018,
      fogNear: 22,
      fogFar: 60,
    });
    this.stars = makeStars(this.kit.lowPower ? 70 : 160, 0x7ad7ff);
    this.kit.scene.add(this.stars);

    const horizon = makeGlowDisc(0x1a6a88, 18, 0.22);
    horizon.position.set(8, -2, -8);
    horizon.scale.set(2.2, 0.55, 1);
    this.kit.scene.add(horizon);

    const water = new THREE.Mesh(
      new THREE.PlaneGeometry(60, 24),
      new THREE.MeshStandardMaterial({
        color: 0x063040,
        roughness: 0.3,
        metalness: 0.45,
        transparent: true,
        opacity: 0.88,
      }),
    );
    water.rotation.x = -Math.PI / 2;
    water.position.set(4, -3.8, 0);
    this.kit.root.add(water);

    const geo = new THREE.PlaneGeometry(36, 3.2, this.segs, 1);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x3dffe0,
      emissive: 0x0a6655,
      emissiveIntensity: 0.55,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.94,
      roughness: 0.22,
      metalness: 0.4,
    });
    this.ribbon = new THREE.Mesh(geo, mat);
    this.ribbon.rotation.x = -Math.PI / 2.15;
    this.kit.root.add(this.ribbon);

    const edgeGeo = new THREE.BufferGeometry();
    edgeGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array((this.segs + 1) * 3), 3));
    const edge = new THREE.Line(
      edgeGeo,
      new THREE.LineBasicMaterial({ color: 0xb8fff0, transparent: true, opacity: 0.85 }),
    );
    edge.name = 'edge';
    this.kit.root.add(edge);

    this.surf = new THREE.Group();
    this.board = new THREE.Mesh(
      new THREE.BoxGeometry(1.7, 0.12, 0.48),
      new THREE.MeshStandardMaterial({ color: 0xff6ad5, emissive: 0x551133, roughness: 0.35 }),
    );
    this.body = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.22, 0.55, 4, 8),
      new THREE.MeshStandardMaterial({ color: 0xa8ffcc, emissive: 0x145533, roughness: 0.45 }),
    );
    this.body.position.y = 0.55;
    this.glow = makeGlowDisc(0x66ffcc, 1.4, 0.4);
    this.glow.rotation.x = -Math.PI / 2;
    this.glow.position.y = 0.05;
    this.surf.add(this.board, this.body, this.glow);
    this.surf.position.set(this.rideX, 0, 0);
    this.kit.root.add(this.surf);

    const sc = this.kit.lowPower ? 40 : 90;
    const spos = new Float32Array(sc * 3);
    const sgeo = new THREE.BufferGeometry();
    sgeo.setAttribute('position', new THREE.BufferAttribute(spos, 3));
    this.spray = new THREE.Points(
      sgeo,
      new THREE.PointsMaterial({
        color: 0xccffff,
        size: 0.12,
        transparent: true,
        opacity: 0.65,
        depthWrite: false,
      }),
    );
    this.kit.root.add(this.spray);

    this.orbit.attach(this.kit.renderer.domElement);
    const rect = ctx.mount.getBoundingClientRect();
    this.resize(rect.width || 320, rect.height || 480);
  }

  private updateRibbon(dt: number, energy: number, bass: number, kick: number): number {
    const heights = this.ocean.tick(dt, energy, bass, kick, this.ctx.getWaveform());
    const pos = this.ribbon.geometry.attributes.position as THREE.BufferAttribute;
    const edge = this.kit.root.getObjectByName('edge') as THREE.Line;
    const edgePos = edge.geometry.attributes.position as THREE.BufferAttribute;
    let rideSurface = 0;

    for (let i = 0; i <= this.segs; i++) {
      const u = i / this.segs;
      const x = -14 + u * 36;
      const y = heights[i];
      const i0 = i;
      const i1 = i + (this.segs + 1);
      pos.setXYZ(i0, x, 1.4, y);
      pos.setXYZ(i1, x, -1.4, y - 0.12);
      edgePos.setXYZ(i, x, y + 0.06, 0);
      if (Math.abs(x - this.rideX) < 36 / this.segs) rideSurface = y;
    }
    pos.needsUpdate = true;
    edgePos.needsUpdate = true;
    this.ribbon.geometry.computeVertexNormals();
    return rideSurface;
  }

  private spawnHazard(energy: number): void {
    const lane = Math.floor(Math.random() * 3) - 1;
    const kind = Math.random() < 0.45 + energy * 0.2 ? 'spike' : 'buoy';
    const g = new THREE.Group();
    if (kind === 'spike') {
      const spike = new THREE.Mesh(
        new THREE.ConeGeometry(0.45, 1.4, 5),
        new THREE.MeshStandardMaterial({ color: 0xff5577, emissive: 0x661122, roughness: 0.4 }),
      );
      spike.position.y = 0.7;
      const glow = makeGlowDisc(0xff6688, 1.1, 0.35);
      glow.rotation.x = -Math.PI / 2;
      g.add(spike, glow);
    } else {
      const buoy = new THREE.Mesh(
        new THREE.SphereGeometry(0.55, 12, 12),
        new THREE.MeshStandardMaterial({ color: 0xffc14d, emissive: 0x664400, roughness: 0.35 }),
      );
      buoy.position.y = 0.55;
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.7, 0.08, 8, 20),
        new THREE.MeshBasicMaterial({ color: 0xffe6a0, transparent: true, opacity: 0.7 }),
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.55;
      g.add(buoy, ring);
    }
    const x = 16 + Math.random() * 5;
    g.position.set(x, 0, lane * 1.35);
    this.kit.root.add(g);
    this.hazards.push({ mesh: g, x, lane, r: kind === 'spike' ? 0.55 : 0.7 });
  }

  private hop(): void {
    if (this.hopping || this.lives <= 0) return;
    this.hopping = true;
    this.hopV = 7.5;
    if (navigator.vibrate) navigator.vibrate(7);
  }

  private hit(): void {
    this.lives -= 1;
    this.invuln = 1.35;
    if (navigator.vibrate) navigator.vibrate([14, 28, 14]);
    if (this.lives <= 0) {
      this.message = 'WIPED OUT — FIRE TO RETRY';
      this.messageT = 99;
    } else {
      this.message = 'SPLASH';
      this.messageT = 0.7;
    }
  }

  private reset(): void {
    this.score = 0;
    this.lives = 3;
    this.wave = 1;
    this.lane = 0;
    this.hopY = 0;
    this.hopV = 0;
    this.hopping = false;
    this.dist = 0;
    this.spawnCd = 0.9;
    this.message = 'CATCH THE WAVE';
    this.messageT = 1.4;
    for (const h of this.hazards) {
      this.kit.root.remove(h.mesh);
      disposeObject(h.mesh);
    }
    this.hazards = [];
  }

  update(dt: number, input: InputState): void {
    if (this.disposed) return;
    const bands = this.ctx.getBands();
    const { energy, bass, kick, beat } = bands;

    if (this.messageT > 0) this.messageT -= dt;
    this.spawnCd = Math.max(0, this.spawnCd - dt);
    this.invuln = Math.max(0, this.invuln - dt);

    if (this.lives <= 0) {
      if (input.firePressed) this.reset();
      this.camTarget.set(this.rideX + 3, 0.5, 0);
      this.orbit.apply(this.kit.camera, this.camTarget, dt);
      this.kit.renderer.render(this.kit.scene, this.kit.camera);
      return;
    }

    const leanSpeed = 5.5;
    if (input.left) this.lane = Math.max(-1, this.lane - leanSpeed * dt);
    if (input.right) this.lane = Math.min(1, this.lane + leanSpeed * dt);
    if (!input.left && !input.right) this.lane *= 1 - Math.min(1, dt * 1.8);

    if (input.firePressed || input.up) this.hop();
    if (this.hopping) {
      this.hopV -= 32 * dt;
      this.hopY += this.hopV * dt;
      if (this.hopY <= 0) {
        this.hopY = 0;
        this.hopV = 0;
        this.hopping = false;
      }
    }

    const scroll = 9 + energy * 7 + this.wave * 0.35;
    this.dist += scroll * dt;
    this.score = Math.floor(this.dist * 4);
    this.wave = 1 + Math.floor(this.dist / 70);

    this.surfaceY = this.updateRibbon(dt, energy, bass, kick);

    const targetZ = this.lane * 1.35;
    this.surf.position.x = this.rideX;
    this.surf.position.y = this.surfaceY + 0.15 + this.hopY;
    this.surf.position.z += (targetZ - this.surf.position.z) * Math.min(1, dt * 12);
    this.surf.rotation.z = -this.lane * 0.22;
    this.surf.rotation.x = -0.12;
    this.body.scale.setScalar(1 + kick * 0.05);
    this.surf.visible = this.invuln <= 0 || Math.floor(this.invuln * 14) % 2 === 0;

    const sp = this.spray.geometry.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < sp.count; i++) {
      let x = sp.getX(i) - scroll * dt * (0.6 + Math.random());
      let y = sp.getY(i) - dt * 2;
      let z = sp.getZ(i);
      if (x < this.rideX - 2 || y < this.surfaceY - 1) {
        x = this.rideX + (Math.random() - 0.3) * 0.8;
        y = this.surfaceY + Math.random() * 0.4;
        z = this.surf.position.z + (Math.random() - 0.5) * 0.8;
      }
      sp.setXYZ(i, x, y, z);
    }
    sp.needsUpdate = true;

    const every = Math.max(0.55, 1.45 - energy * 0.45 - this.wave * 0.02);
    if (this.spawnCd <= 0 || (beat > 0.2 && this.spawnCd < every * 0.5)) {
      this.spawnHazard(energy);
      this.spawnCd = every * (0.8 + Math.random() * 0.45);
    }

    for (const h of this.hazards) {
      h.x -= scroll * dt;
      const u = (h.x + 14) / 36;
      const sy = this.ocean.sampleAt(Math.max(0, Math.min(1, u)));
      h.mesh.position.x = h.x;
      h.mesh.position.y = sy;
      h.mesh.position.z = h.lane * 1.35;
      h.mesh.rotation.y += dt * 1.2;
    }

    if (this.invuln <= 0 && this.hopY < 0.55) {
      for (const h of this.hazards) {
        const dx = Math.abs(h.x - this.rideX);
        const dz = Math.abs(h.lane * 1.35 - this.surf.position.z);
        if (dx < 0.85 + h.r * 0.3 && dz < 0.75 + h.r * 0.25) {
          this.hit();
          h.mesh.visible = false;
        }
      }
    }

    this.hazards = this.hazards.filter((h) => {
      if (!h.mesh.visible || h.x < -16) {
        this.kit.root.remove(h.mesh);
        disposeObject(h.mesh);
        return false;
      }
      return true;
    });

    this.camTarget.set(this.rideX + 2.5, this.surfaceY * 0.35 + 0.4, this.surf.position.z * 0.25);
    this.orbit.apply(this.kit.camera, this.camTarget, dt);
    this.kit.renderer.render(this.kit.scene, this.kit.camera);
  }

  resize(w: number, h: number): void {
    if (!this.kit) return;
    resizeThree(this.kit, w, h, 18, 22);
  }

  getHud() {
    return {
      score: this.score,
      lives: Math.max(0, this.lives),
      wave: this.wave,
      message: this.messageT > 0 ? this.message : undefined,
    };
  }

  dispose(): void {
    this.disposed = true;
    this.orbit.detach();
    for (const h of this.hazards) disposeObject(h.mesh);
    this.hazards = [];
    this.kit?.dispose();
  }
}
